import React, { useEffect, useState } from "react";
import { listAllTemplates } from "../../api/admin/templates";
import Loader from "../../components/common/loader/loader";
import Error from "../../components/common/error/error";
import { siteSettings } from "../../settings/site-settings";
import TableHeaders from "../../components/ui/table-headers";
import { TableColumn } from "../../interfaces/tables";
import Table from "../../components/ui/table";
import { Button } from "../ui/button";
import { GoCheckCircleFill, GoXCircleFill } from "react-icons/go";

function SelectTemplateTable({ onTemplateSelected }: any) {
  // fetch templates from server
  const [templates, setTemplates] = useState([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(5);

  const [filters, setFilters] = React.useState<any>({}); // pre-made filters published/unpublished

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  const loadTemplates = async () => {
    const response = await listAllTemplates({
      page,
      sort,
      search,
      pageSize,
      filters,
    });

    if (!response || response.status !== 200) {
      setError("Error loading templates");
      setLoading(false);
      return;
    }

    setTemplates(response.data?.data);
    setTableData(formatDataForTable(response.data?.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
    setError(null);
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handlePublishedFilterChange = (key: string) => {
    if (key === "all") {
      setFilters({});
    } else {
      setFilters({ isPublished: key === "published" ? true : false });
    }

    setPage(1); // reset page to 1 when filter changes
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  useEffect(() => {
    loadTemplates();
  }, [page, sort, search, pageSize, filters]);

  const columns: TableColumn[] = [
    {
      key: "previewImage",
      label: "Preview Image",
      render: (cell, row) => {
        if (cell) {
          return (
            <div>
              <div className="group flex w-min hover:cursor-zoom-in relative">
                <img
                  src={cell}
                  alt="category"
                  className="h-[50px] max-w-[50px] object-cover rounded-md"
                />

                <div className="opacity-0 group-hover:opacity-100 fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 bg-borderColor border border-white rounded w-[500px] h-[500px] transition-opacity duration-0 ease-in group-hover:duration-500 group-hover:ease-out pointer-events-none">
                  <img
                    src={cell}
                    alt="category"
                    className="w-full h-full object-contain rounded-md"
                  />
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="w-[35px] h-[50px] bg-borderColor rounded-md flex items-center justify-center text-gray-500">
              N/A
            </div>
          );
        }
      },
    },

    { key: "_id", label: "Internal Id" },

    { key: "internalName", label: "Internal Name" },

    {
      key: "isPublished",
      label: "Published",

      render: (cell, row) => {
        return (
          <div className="flex items-center">
            {cell ? (
              <GoCheckCircleFill size={20} className="text-green-500" />
            ) : (
              <GoXCircleFill size={20} className="text-red-500" />
            )}
          </div>
        );
      },
    },

    { key: "slug", label: "Slug" },

    {
      key: "btn",
      label: "Actions",
      //   fixed: true,
      render: (cell, row) => {
        return (
          <div className="flex gap-x-4">
            <Button onClick={() => onTemplateSelected(row._id)}>
              Select Template
            </Button>
          </div>
        );
      },
    },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,
        internalName: d.internalName,
        slug: d.slug,
        isPublished: d.isPublished,
        tags: d.tags,
        createdAt: d.createdAt,
        previewImage: d.previewImage,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  return (
    <div className="w-full h-full ">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto ">
            <h1 className="text-2xl font-bold text-primary">Templates</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={siteSettings.tables.templates.searchByOptions}
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.templates.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={siteSettings.tables.templates.pageSizeOptions}
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={siteSettings.tables.templates.pageSizeOptions[0]}
              showPublishedFilter={true}
              onPublishedFilterChange={handlePublishedFilterChange}
              currentPublishedFilterValue={
                filters.isPublished === true
                  ? "published"
                  : filters.isPublished === false
                  ? "unpublished"
                  : "all"
              }
            />

            <Table columns={columns} data={tableData} />
          </div>
        </div>
      )}
    </div>
  );
}

export default SelectTemplateTable;
